import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Loader2, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CompanyBrandingSection } from "@/components/CompanyBrandingSection";
import { CompanyPaySection, paySettingsFrom, paySettingsPayload } from "@/components/CompanyPaySection";
import { JobTypesEditor } from "@/components/JobTypesEditor";
import { saveJobTypes } from "@/components/JobTypesDialog";
import { WelcomeClients } from "@/components/WelcomeClients";
import { CompanyModulesPicker } from "@/components/CompanyModulesPicker";
import { useClientImport } from "@/components/clients/useClientImport";
import { api } from "@/lib/api";
import { BRAND, applyBrandColor, isDarkEnough } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { useDepot } from "@/context/DepotContext";

const DepotNames = ({ existing, names, setNames, v, setV }) => {
  const add = () => {
    const n = v.trim();
    if (n && ![...existing, ...names].some((x) => x.toLowerCase() === n.toLowerCase())) setNames([...names, n]);
    setV("");
  };
  return (
    <div className="rounded-lg border border-border p-4 space-y-3" data-testid="welcome-depots">
      <div>
        <p className="text-sm font-medium">Depots <span className="text-muted-foreground font-normal">(optional)</span></p>
        <p className="text-xs text-muted-foreground">Work from more than one location? Add them now, or later from the Depots tab.</p>
      </div>
      {existing.length > 0 && (
        <p className="text-xs" data-testid="welcome-existing-depots">
          <span className="text-muted-foreground">Already set up: </span><span className="font-medium">{existing.join(", ")}</span>
        </p>
      )}
      <div className="flex gap-2">
        <Input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="e.g. Ballarat" data-testid="welcome-depot-input" />
        <Button type="button" variant="outline" onClick={add} className="rounded-full shrink-0" data-testid="welcome-depot-add"><Plus className="w-4 h-4" /></Button>
      </div>
      {names.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {names.map((n) => (
            <span key={n} className="inline-flex items-center gap-1.5 bg-secondary rounded-full pl-3 pr-1.5 py-1 text-xs" data-testid={`welcome-depot-chip-${n}`}>
              {n}<button type="button" onClick={() => setNames(names.filter((x) => x !== n))} className="w-4 h-4 inline-flex items-center justify-center rounded-full hover:bg-destructive/10 text-destructive"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// One-time, skippable setup for a newly signed-up company login.
export const WelcomeSetup = () => {
  const { user } = useAuth();
  const { company, setCompany } = useCompany();
  const { reload, depots } = useDepot();
  const existing = depots.map((d) => d.name);
  const open = !!(user?.is_company_admin && company && company.setup_done === false);
  const [name, setName] = useState("");
  const [color, setColor] = useState(BRAND.color);
  const [depotNames, setDepotNames] = useState([]);
  const [pending, setPending] = useState("");
  const [busy, setBusy] = useState(false);
  const [paySet, setPaySet] = useState(() => paySettingsFrom(company));
  const [types, setTypes] = useState([]);
  const [mods, setMods] = useState([]); // basics only until they tick more
  const withClients = mods.includes("clients");
  const imp = useClientImport();
  const [clientDepot, setClientDepot] = useState("");
  const allDepots = [...existing, ...depotNames];
  const importTo = allDepots.includes(clientDepot) ? clientDepot : allDepots[0] || "";

  useEffect(() => {
    if (open) { setName(company.name === BRAND.name ? "" : company.name); setColor(company.color || BRAND.color); setPaySet(paySettingsFrom(company)); setTypes(company.job_types || []); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => { if (open && isDarkEnough(color)) applyBrandColor(color); }, [open, color]);

  const skip = async () => {
    setBusy(true);
    try { setCompany(await api.updateCompany({ setup_done: true })); }
    catch { toast.error("Could not skip — please try again"); }
    finally { setBusy(false); }
  };

  const importClients = async (made) => {
    const key = importTo.toLowerCase();
    const id = made[key] || depots.find((d) => d.name.toLowerCase() === key)?.id;
    if (!id) { toast.error(`Depot "${importTo}" wasn't added, so your clients weren't imported — import them later from the Clients tab`); return; }
    const r = await imp.run(id);
    if (r) toast.success(`Added ${r.added} client${r.added !== 1 ? "s" : ""}${r.depot_name ? ` to ${r.depot_name}` : ""}`);
  };

  const finish = async () => {
    if (!name.trim()) { toast.error("Add your company name"); return; }
    if (!isDarkEnough(color)) { toast.error("Pick a darker main colour"); return; }
    if (withClients && imp.sheet && !imp.hasName) { toast.error("Pick which column has the client name, or don't import the file"); return; }
    setBusy(true);
    if (mods.includes("jobs") && !(await saveJobTypes(types, setCompany))) { setBusy(false); return; }
    try {
      const extra = pending.trim();
      const all = extra && ![...existing, ...depotNames].some((x) => x.toLowerCase() === extra.toLowerCase()) ? [...depotNames, extra] : depotNames;
      const made = {};
      for (const d of all) {
        const r = await api.createDepot(d).catch(() => { toast.error(`Could not add depot "${d}" — add it from the Depots tab`); return null; });
        if (r) made[d.toLowerCase()] = r.id;
      }
      if (withClients && imp.sheet && imp.clients.length) await importClients(made);
      setCompany(await api.updateCompany({ name: name.trim(), color, ...paySettingsPayload(paySet), modules: mods, setup_done: true }));
      await reload();
      toast.success("You're all set — welcome to Cross Jobs");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save your setup"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && skip()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="welcome-setup-dialog" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> Welcome to Cross Jobs</DialogTitle>
          <DialogDescription>Make the app yours in a minute: name, logo, colour, the features you want and how you pay your crew. You can change any of it later in Settings.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 min-w-0">
          <CompanyBrandingSection name={name} setName={setName} color={color} setColor={setColor} />
          <div className="rounded-lg border border-border p-4 space-y-3" data-testid="welcome-modules">
            <div>
              <p className="text-sm font-medium">What do you need?</p>
              <p className="text-xs text-muted-foreground">Basic is always included. Tick anything else you want now, and add or remove features any time in Settings.</p>
            </div>
            <CompanyModulesPicker value={mods} onChange={setMods} disabled={busy} />
          </div>
          <DepotNames existing={existing} names={depotNames} setNames={setDepotNames} v={pending} setV={setPending} />
          {withClients && <WelcomeClients imp={imp} depotNames={allDepots} depot={importTo} setDepot={setClientDepot} />}
          {mods.includes("jobs") && <div className="rounded-lg border border-border p-4 space-y-3" data-testid="welcome-job-types">
            <div>
              <p className="text-sm font-medium">Job types</p>
              <p className="text-xs text-muted-foreground">The kinds of jobs you send your crew. Pick your industry for a starter list, then rename, add or remove.</p>
            </div>
            <JobTypesEditor items={types} onChange={setTypes} />
          </div>}
          <CompanyPaySection value={paySet} onChange={setPaySet} />
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={skip} disabled={busy} className="rounded-full" data-testid="welcome-skip-btn">Skip for now</Button>
          <Button onClick={finish} disabled={busy} className="rounded-full bg-primary hover:bg-primary/90 text-white" data-testid="welcome-finish-btn">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Finish setup
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
